import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Progress, Tag, Space, Spin, Result, Row, Col, Typography, List } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { checklistApi } from '@tradermind/shared/api';

const { Title, Text } = Typography;

export default function ChecklistResultPage() {
  const { t } = useTranslation(['checklist', 'strategy']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['checklist', 'result', id],
    queryFn: () => checklistApi.getChecklistResult(Number(id)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <Result
        status="404"
        title={t('result_page.not_found')}
        subTitle={t('result_page.not_found_desc')}
        extra={
          <Button type="primary" onClick={() => navigate('/checklist/pre-trade')}>
            {t('result_page.recheck')}
          </Button>
        }
      />
    );
  }

  const result = data.data;

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  // 根据 passed 和 quality_level 推断 recommendation
  const getRecommendation = () => {
    if (result.recommendation) return result.recommendation;
    if (result.passed) {
      if (result.quality_score >= 70) return 'proceed';
      return 'caution';
    }
    return 'avoid';
  };

  // 根据 risk_score 推断 risk_level
  const getRiskLevel = () => {
    if (result.risk_level) return result.risk_level;
    if (result.risk_score <= 30) return 'low';
    if (result.risk_score <= 60) return 'medium';
    return 'high';
  };

  // 将 score_breakdown 对象转换为 breakdown 数组
  const getBreakdown = () => {
    if (result.breakdown) return result.breakdown;
    if (result.score_breakdown) {
      const sb = result.score_breakdown;
      return [
        { category: t('result_page.logic_score'), score: sb.logic_score, max_score: 100 },
        { category: t('result_page.risk_control'), score: sb.risk_control, max_score: 100 },
        { category: t('result_page.emotion_score'), score: sb.emotion_score, max_score: 100 },
        { category: t('result_page.position_score'), score: sb.position_score, max_score: 100 },
      ];
    }
    return [];
  };

  // 将 suggestions 对象数组转换为可显示的内容
  const getSuggestions = (): string[] => {
    if (!result.suggestions || result.suggestions.length === 0) return [];
    // 如果是字符串数组，直接返回
    if (typeof result.suggestions[0] === 'string') return result.suggestions as unknown as string[];
    // 如果是对象数组，提取 title 和 description
    return (result.suggestions as Array<{ title: string; description: string }>).map((s) => `${s.title}: ${s.description}`);
  };

  const getRecommendationConfig = (rec: string) => {
    switch (rec) {
      case 'proceed':
        return {
          icon: <CheckCircleOutlined style={{ fontSize: 48, color: '#10B981' }} />,
          title: t('result_page.rec_proceed'),
          description: t('result_page.rec_proceed_desc'),
          color: '#10B981',
        };
      case 'caution':
        return {
          icon: <WarningOutlined style={{ fontSize: 48, color: '#F59E0B' }} />,
          title: t('result_page.rec_caution'),
          description: t('result_page.rec_caution_desc'),
          color: '#F59E0B',
        };
      case 'avoid':
        return {
          icon: <CloseCircleOutlined style={{ fontSize: 48, color: '#EF4444' }} />,
          title: t('result_page.rec_avoid'),
          description: t('result_page.rec_avoid_desc'),
          color: '#EF4444',
        };
      default:
        return {
          icon: <CheckCircleOutlined style={{ fontSize: 48, color: '#6B7280' }} />,
          title: '-',
          description: '',
          color: '#6B7280',
        };
    }
  };

  const recommendation = getRecommendation();
  const riskLevel = getRiskLevel();
  const breakdown = getBreakdown();
  const suggestions = getSuggestions();
  const recConfig = getRecommendationConfig(recommendation);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/checklist/pre-trade')}>
            {t('result_page.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('result_page.title')}
          </Title>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              {recConfig.icon}
              <Title level={3} style={{ color: recConfig.color, marginTop: 16 }}>
                {recConfig.title}
              </Title>
              <Text type="secondary">{recConfig.description}</Text>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <Progress
                type="circle"
                percent={result.quality_score}
                strokeColor={getScoreColor(result.quality_score)}
                format={(percent) => (
                  <div>
                    <div style={{ fontSize: 32, fontWeight: 700 }}>{percent}</div>
                    <div style={{ fontSize: 14, color: '#999' }}>{t('result_page.quality_score')}</div>
                  </div>
                )}
                size={160}
              />
            </div>

            {breakdown.length > 0 && (
              <>
                <Title level={5}>{t('result_page.score_breakdown')}</Title>
                <List
                  dataSource={breakdown}
                  renderItem={(item) => (
                    <List.Item>
                      <div style={{ flex: 1 }}>
                        <Text>{item.category}</Text>
                      </div>
                      <div style={{ width: 200 }}>
                        <Progress
                          percent={(item.score / item.max_score) * 100}
                          format={() => `${item.score}/${item.max_score}`}
                          strokeColor={getScoreColor((item.score / item.max_score) * 100)}
                        />
                      </div>
                    </List.Item>
                  )}
                />
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('result_page.risk_level')}>
            <div style={{ textAlign: 'center' }}>
              <Tag
                color={
                  riskLevel === 'low'
                    ? 'green'
                    : riskLevel === 'medium'
                    ? 'orange'
                    : 'red'
                }
                style={{ fontSize: 16, padding: '8px 24px' }}
              >
                {riskLevel === 'low'
                  ? t('result_page.risk_low')
                  : riskLevel === 'medium'
                  ? t('result_page.risk_medium')
                  : t('result_page.risk_high')}
              </Tag>
            </div>
          </Card>

          {result.ai_logic_feedback && (
            <Card title={`🤖 ${t('strategy:ai_evaluation')}`} style={{ marginTop: 24 }}>
              {result.ai_logic_feedback.completeness && (() => {
                const completeness = result.ai_logic_feedback!.completeness!;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{t('strategy:completeness')}</Text>
                    <Progress
                      percent={(completeness.score / 10) * 100}
                      format={() => `${completeness.score}/10`}
                      strokeColor={completeness.score >= 7 ? '#10B981' : completeness.score >= 5 ? '#F59E0B' : '#EF4444'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {completeness.reason}
                    </Text>
                  </div>
                );
              })()}
              {result.ai_logic_feedback.consistency && (() => {
                const consistency = result.ai_logic_feedback!.consistency!;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{t('strategy:consistency')}</Text>
                    <Progress
                      percent={(consistency.score / 10) * 100}
                      format={() => `${consistency.score}/10`}
                      strokeColor={consistency.score >= 7 ? '#10B981' : consistency.score >= 5 ? '#F59E0B' : '#EF4444'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {consistency.reason}
                    </Text>
                  </div>
                );
              })()}
              {result.ai_logic_feedback.strategy_fit && (() => {
                const strategyFit = result.ai_logic_feedback!.strategy_fit!;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{t('strategy:strategy_fit')}</Text>
                    <Progress
                      percent={(strategyFit.score / 10) * 100}
                      format={() => `${strategyFit.score}/10`}
                      strokeColor={strategyFit.score >= 7 ? '#10B981' : strategyFit.score >= 5 ? '#F59E0B' : '#EF4444'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {strategyFit.reason}
                    </Text>
                  </div>
                );
              })()}
              {result.ai_logic_feedback.risk_awareness && (() => {
                const riskAwareness = result.ai_logic_feedback!.risk_awareness!;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>{t('strategy:risk_awareness')}</Text>
                    <Progress
                      percent={(riskAwareness.score / 10) * 100}
                      format={() => `${riskAwareness.score}/10`}
                      strokeColor={riskAwareness.score >= 7 ? '#10B981' : riskAwareness.score >= 5 ? '#F59E0B' : '#EF4444'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {riskAwareness.reason}
                    </Text>
                  </div>
                );
              })()}
              {result.ai_logic_feedback.overall_suggestion && (
                <Card size="small" style={{ backgroundColor: '#f0f9ff', border: 'none' }}>
                  <Text>💡 {result.ai_logic_feedback.overall_suggestion}</Text>
                </Card>
              )}
            </Card>
          )}

          {suggestions.length > 0 && (
            <Card title={t('result_page.suggestions')} style={{ marginTop: 24 }}>
              <List
                dataSource={suggestions}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item}</Text>
                  </List.Item>
                )}
              />
            </Card>
          )}

          <Card title={t('result_page.next_steps')} style={{ marginTop: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {recommendation === 'proceed' && (
                <Button
                  type="primary"
                  block
                  onClick={() =>
                    navigate(
                      `/trades/new?checklist_id=${encodeURIComponent(String(result.id || result.checklist_id))}` +
                        `&quality_score=${encodeURIComponent(String(result.quality_score))}` +
                        `&symbol=${encodeURIComponent(String(result.symbol || ''))}` +
                        `&direction=${encodeURIComponent(String(result.direction || ''))}`,
                      {
                        state: {
                          checklist_id: result.id || result.checklist_id,
                          quality_score: result.quality_score,
                          symbol: result.symbol,
                          direction: result.direction,
                        },
                      }
                    )
                  }
                >
                  {t('result_page.record_trade')}
                </Button>
              )}
              {recommendation === 'avoid' && (
                <Button type="primary" danger block onClick={() => navigate('/cooldown')}>
                  {t('result_page.enter_cooldown')}
                </Button>
              )}
              <Button block onClick={() => navigate('/checklist/pre-trade')}>
                {t('result_page.recheck')}
              </Button>
              <Button block onClick={() => navigate('/dashboard')}>
                {t('result_page.back_home')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {result.ai_logic_feedback && (
        <div style={{ marginTop: 24, textAlign: 'center', padding: '12px 0', backgroundColor: '#fef3c7', borderRadius: 8 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            ⚠️ {t('strategy:ai_disclaimer')}
          </Text>
        </div>
      )}
    </div>
  );
}
